import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { MessageSquare, ClipboardList, Target, BadgeCheck, Check, X, ArrowRight, ArrowLeft, MapPin, ScanLine } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { METHOD_STEPS, methodBySlug } from "../data/method";
import { IMAGES, SERVICE_AREAS } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";

const ICONS = { MessageSquare, ClipboardList, Target, BadgeCheck };
const DOMAIN = "https://www.speedyresurfacing.com.au";

export default function MethodDetail() {
  const { slug } = useParams();
  const step = methodBySlug(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!step) return (
    <div className="pt-40 pb-24 text-center">
      <h1 className="font-heading font-black text-4xl">Step not found</h1>
      <Link to="/method" className="mt-6 inline-block text-neon">← The Speedy Method</Link>
    </div>
  );

  const Icon = ICONS[step.icon];
  const idx = METHOD_STEPS.findIndex((s) => s.slug === step.slug);
  const prev = METHOD_STEPS[idx - 1];
  const next = METHOD_STEPS[idx + 1];

  return (
    <div className="min-h-screen noise" data-testid={`method-detail-${step.slug}`}>
      <Seo
        title={`${step.seoTitle} | Speedy Resurfacing`}
        description={step.seoDescription}
        path={`/method/${step.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HowTo",
              name: "The Speedy Method — court resurfacing in four steps",
              url: `${DOMAIN}/method`,
              step: {
                "@type": "HowToStep",
                name: step.title,
                position: idx + 1,
                description: step.seoDescription,
                url: `${DOMAIN}/method/${step.slug}`,
                itemListElement: step.whatHappens.map((w) => ({
                  "@type": "HowToDirection", text: `${w.title}. ${w.text}`,
                })),
              },
            },
            {
              "@type": "FAQPage",
              mainEntity: step.faqs.map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN + "/" },
                { "@type": "ListItem", position: 2, name: "The Speedy Method", item: DOMAIN + "/method" },
                { "@type": "ListItem", position: 3, name: step.title, item: `${DOMAIN}/method/${step.slug}` },
              ],
            },
            {
              "@type": "LocalBusiness",
              name: "Speedy Resurfacing",
              telephone: "0466 495 495",
              email: "info@speedyresurfacing.com.au",
              url: DOMAIN,
              areaServed: SERVICE_AREAS,
            },
          ],
        }}
      />

      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <img {...responsive(IMAGES[step.image], "100vw", [640, 1000, 1600])} decoding="async" alt={`${step.title} — court resurfacing Sydney`} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/40" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <nav className="flex flex-wrap items-center gap-2 font-mono text-xs text-zinc-500 mb-6" data-testid="method-detail-breadcrumb">
            <Link to="/" className="hover:text-neon">Home</Link><span>/</span>
            <Link to="/method" className="hover:text-neon">The Speedy Method</Link><span>/</span>
            <span className="text-zinc-300">{step.title}</span>
          </nav>
          <Overline>Step {step.n} of the Speedy Method</Overline>
          <div className="mt-5 flex items-start gap-5">
            <span className="hidden md:flex h-16 w-16 shrink-0 items-center justify-center rounded-sm bg-gold/10 text-gold"><Icon size={30} /></span>
            <h1 className="font-heading font-black text-5xl md:text-7xl tracking-tighter">{step.title}</h1>
          </div>
          <p className="mt-6 max-w-2xl text-lg text-zinc-300">{step.tagline}</p>
          <span className="mt-6 inline-block rounded-sm border border-gold/30 bg-obsidian-800/70 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-gold">
            {step.timeline}
          </span>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 md:px-10 pb-24">
        {/* Intro + stats */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Reveal className="lg:col-span-2">
            <div className="rounded-lg border border-white/10 bg-obsidian-800 p-8 h-full">
              <span className="font-mono text-xs uppercase tracking-widest text-neon">Why this step matters</span>
              <p className="mt-4 text-zinc-300">{step.intro}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid gap-4 h-full">
              {step.stats.map((s) => (
                <div key={s.k} className="rounded-lg border border-gold/20 bg-obsidian-800 p-6">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</span>
                  <p className="mt-1 font-heading font-black text-2xl gold-text">{s.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* What actually happens */}
        <div className="mt-16">
          <Reveal>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight">What Actually Happens</h2>
            <p className="mt-3 max-w-xl text-zinc-400">No mystery, no jargon — the real sequence on your court.</p>
          </Reveal>
          <div className="mt-8 space-y-4">
            {step.whatHappens.map((w, i) => (
              <Reveal key={w.title} delay={Math.min(i, 4) * 0.05}>
                <div data-testid={`method-happens-${i}`} className="group flex gap-5 rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/30 transition-colors">
                  <span className="font-heading font-black text-2xl text-zinc-700 group-hover:text-gold/50 transition-colors shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-lg md:text-xl">{w.title}</h3>
                    <p className="mt-2 text-sm md:text-base text-zinc-300">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Included / receive */}
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-lg border border-neon/20 bg-obsidian-800 p-8">
              <h3 className="font-heading font-bold text-xl mb-5">What's Included</h3>
              <ul className="space-y-3" data-testid="method-includes">
                {step.includes.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-zinc-300"><Check size={16} className="text-neon mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-lg border border-gold/20 bg-obsidian-800 p-8">
              <h3 className="font-heading font-bold text-xl mb-5">What You Receive</h3>
              <ul className="space-y-3" data-testid="method-deliverables">
                {step.deliverables.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-zinc-300"><Check size={16} className="text-gold mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* Showcase */}
        <div className="mt-16">
          <Reveal>
            <div className="overflow-hidden rounded-lg border border-white/10">
              <img {...responsive(IMAGES[step.showcase], "(min-width: 1024px) 50vw, 100vw", [480, 800, 1200])} loading="lazy" decoding="async" alt={`${step.title} result — court resurfacing and line marking Sydney`}
                   className="h-[280px] md:h-[460px] w-full object-cover" />
            </div>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{step.showcaseCaption}</p>
          </Reveal>
        </div>

        {/* Mistakes we never make */}
        <div className="mt-16">
          <Reveal>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Mistakes We Never Make</h2>
            <p className="mt-3 max-w-xl text-zinc-400">The shortcuts that ruin courts — and why you will not see them here.</p>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="method-mistakes">
            {step.mistakes.map((m, i) => (
              <Reveal key={m} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-obsidian-800 p-6">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-400"><X size={15} /></span>
                  <p className="text-sm text-zinc-300">{m}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Local SEO */}
        <div className="mt-16 rounded-lg border border-white/10 bg-obsidian-800 p-8">
          <Reveal>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight flex items-center gap-2">
              <MapPin className="text-neon" size={22} /> {step.title} across Sydney &amp; NSW
            </h2>
            <p className="mt-4 max-w-3xl text-zinc-300">{step.local}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {SERVICE_AREAS.map((a) => (
                <span key={a} className="rounded-sm border border-white/10 px-3 py-1.5 text-sm text-zinc-400">{a}</span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <Reveal><h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-6">{step.title} FAQ</h2></Reveal>
          <Accordion type="single" collapsible className="rounded-lg border border-white/10 bg-obsidian-800 px-6" data-testid="method-faq">
            {step.faqs.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-heading font-semibold hover:text-neon">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Prev / next */}
        <div className="mt-16 grid sm:grid-cols-2 gap-4">
          {prev ? (
            <Link to={`/method/${prev.slug}`} data-testid="method-prev" className="group rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/40 transition-colors">
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                <ArrowLeft size={14} /> Step {prev.n}
              </span>
              <p className="mt-2 font-heading font-bold text-xl group-hover:text-neon transition-colors">{prev.title}</p>
            </Link>
          ) : (
            <Link to="/method" data-testid="method-prev" className="group rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/40 transition-colors">
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                <ArrowLeft size={14} /> Overview
              </span>
              <p className="mt-2 font-heading font-bold text-xl group-hover:text-neon transition-colors">The Speedy Method</p>
            </Link>
          )}
          {next ? (
            <Link to={`/method/${next.slug}`} data-testid="method-next" className="group rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/40 transition-colors sm:text-right">
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 sm:justify-end">
                Step {next.n} <ArrowRight size={14} />
              </span>
              <p className="mt-2 font-heading font-bold text-xl group-hover:text-neon transition-colors">{next.title}</p>
            </Link>
          ) : (
            <Link to="/gallery" data-testid="method-next" className="group rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/40 transition-colors sm:text-right">
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 sm:justify-end">
                Next <ArrowRight size={14} />
              </span>
              <p className="mt-2 font-heading font-bold text-xl group-hover:text-neon transition-colors">See Finished Courts</p>
            </Link>
          )}
        </div>

        {/* CTA */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Link to="/quote" data-testid="method-detail-quote" className="flex items-center justify-between rounded-lg bg-neon p-8 text-obsidian shadow-neon group">
            <span className="font-heading font-black text-2xl">Request a Free Quote</span>
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={28} />
          </Link>
          <Link to="/court-scan" data-testid="method-detail-scan" className="flex items-center justify-between rounded-lg border border-neon/30 bg-obsidian-800 p-8 text-white hover:border-neon transition-colors group">
            <span className="font-heading font-black text-2xl">Try Court Scan™</span>
            <ScanLine className="text-neon" size={28} />
          </Link>
        </div>
      </div>
    </div>
  );
}
